// Severity classification for the result-page warning banners.
//
// These messages previously rendered as a flat stack of .badge blocks in source
// order, which put "totals may be understated" (act now) in the same visual tier
// as the reference-lookup note (nothing to do). Ranking them is the whole point:
// a user must be able to triage without reading all seven.
//
// The message TEXT here is copied verbatim from what it replaced — this module
// ranks and titles, it does not reword. No figure is derived; the opening-gap banner
// only formats the engine's own.
//
// Pure and data-only so it can be asserted from Python the way multiSearch.ts is
// (see tests/alerts_port.py + tests/test_alert_severity.py). Keep the two in
// lockstep; the port mirrors every function below by name.

import { fmtAed } from "../components/KpiCard";
import type { ReconResult } from "../types/api";

export type Severity = "critical" | "warning" | "info";

export interface Alert {
  severity: Severity;
  /** Stable identifier — used as the React key and by the tests. */
  id: string;
  /** Short uppercase label rendered before the body. */
  title: string;
  body: string;
}

// critical = a figure may be wrong, or an action is required to fix the run.
// warning  = the run is usable but something should be checked before relying on it.
// info     = stated for completeness; no action, no figure affected.
const RANK: Record<Severity, number> = { critical: 0, warning: 1, info: 2 };

export function severityRank(s: Severity): number {
  return RANK[s];
}

/** Builds the ranked alert list for a result. Derives no figure. */
export function buildAlerts(
  result: ReconResult,
  opts: { applyError?: string | null; filesLost?: boolean } = {},
): Alert[] {
  const out: Alert[] = [];

  if (opts.applyError) {
    out.push({
      severity: "critical",
      id: "apply-error",
      title: "Could not apply change",
      body: opts.applyError,
    });
  }

  if (opts.filesLost) {
    out.push({
      severity: "warning",
      id: "files-lost",
      title: "Restored after refresh",
      body:
        "Restored after a page refresh — your matches and notes are intact. The " +
        "uploaded files themselves aren't, so the sidebar settings and auto-match " +
        "removals are locked. Use Re-upload to change those.",
    });
  }

  // Uploaded files no reader could open: their rows are in no total at all.
  const unreadable = result.unreadable_files;
  if (Array.isArray(unreadable) && unreadable.length > 0) {
    out.push({
      severity: "critical",
      id: "unreadable-files",
      title: "Unreadable files",
      body:
        `Unreadable files — ${unreadable.length} uploaded file(s) could not be read and were NOT included in this reconciliation: ${unreadable.join(", ")}. ` +
        "Their transactions are missing from every total. Re-export the file (or save it as .xlsx) and re-upload.",
    });
  }

  // Statements that were reconciled but do not reproduce their own printed figures.
  const tieOut = result.statement_warnings;
  if (Array.isArray(tieOut) && tieOut.length > 0) {
    out.push({
      severity: "critical",
      id: "statement-tie-out",
      title: "Statement does not tie out",
      body:
        "Statement tie-out — these files were reconciled, but their rows do not reproduce the statement's own printed figures: " +
        tieOut.map((w) => `${w.file}: ${w.message}`).join(" · ") +
        ". Verify the source file before relying on the totals.",
    });
  }

  // The run broke an identity its own figures must satisfy: a bug, not a data issue.
  const selfCheck = result.self_check;
  if (Array.isArray(selfCheck) && selfCheck.length > 0) {
    out.push({
      severity: "critical",
      id: "self-check",
      title: "Self-check failed",
      body:
        "Self-check failed — " +
        selfCheck
          .map(
            (f) =>
              `the ${f.track} Matched line nets ${fmtAed(f.amount)}, more than its amount tolerance allows (${fmtAed(f.bound)})`,
          )
          .join(" · ") +
        ". Matched pairs cancel, so a figure on this reconciliation is wrong. Do not rely on it; send these files to the reconciliation team.",
    });
  }

  const priorYear = result.prior_year_sheets;
  if (Array.isArray(priorYear) && priorYear.length > 0) {
    out.push({
      severity: "critical",
      id: "prior-year-sheets",
      title: "Prior-year sheets",
      body:
        `Prior-year sheets detected and reconciled: ${priorYear.join(", ")}. These sheets ` +
        "name an earlier year than the newest sheet uploaded — if they are out of scope, delete them " +
        "from the source file and re-upload.",
    });
  }

  const chain = result.chain_warning;
  if (chain) {
    out.push({
      severity: "critical",
      id: "chain-warning",
      title: "Chain warning",
      body:
        chain.kind === "duplicate"
          ? `Chain warning — the ${chain.n_files} uploaded month files include the same period more than once, so they do not form one contiguous run. The headline closing fell back to a plain sum and double-counts the shared openings — remove the duplicate month file and re-upload.`
          : `Chain warning — the ${chain.n_files} uploaded month files do not form one contiguous run (a month in between is missing). The headline closing fell back to a plain sum and double-counts the shared openings — upload the missing month and re-upload.`,
    });
  }

  // The previous month's output closed where this run should open; any gap reaches the
  // Overview as Net Adjustment != Gross Variance with no row behind it. Figures are the
  // engine's, formatted only.
  const gap = result.prior_output_opening_gap;
  if (gap) {
    const parts: string[] = [];
    for (const [name, book] of [["Insurer", gap.insurer], ["CRI", gap.cri]] as const) {
      if (book.difference) {
        const listed = book.listed_before_period
          ? `, ${fmtAed(book.listed_before_period)} of it listed on Matched as settled before the period`
          : "";
        parts.push(
          `${name} opening ${fmtAed(book.opening)} against the previous output's closing ` +
            `${fmtAed(book.previous_closing)}${listed} (difference ${fmtAed(book.difference)})`,
        );
      }
    }
    if (gap.not_carried) {
      parts.push(
        `${fmtAed(gap.not_carried)} on the previous output's Others line, which is not carried forward`,
      );
    }
    if (gap.previous_walkdown_gap) {
      parts.push(
        `${fmtAed(gap.previous_walkdown_gap)} by which the previous output's own walk-down did not close`,
      );
    }
    out.push({
      severity: "warning",
      id: "opening-gap",
      title: "Opening balance check",
      body:
        "Opening balance check — this run does not open where the previous month's output closed, " +
        `which puts ${fmtAed(gap.walkdown_effect)} between the Overview's Gross Variance and its Net Adjustment: ` +
        parts.join(" · ") +
        ". Check both months' statements cover the same accounts (e.g. a customer block missing from one of them).",
    });
  }

  // Items the uploaded previous output listed with only their six keys: their other
  // columns are not in that file. The count is the engine's; nothing is derived here.
  const unrec = result.unreconciled;
  const keyOnly = (unrec?.rolled_key_only_count || 0) + (unrec?.cri_rolled_key_only_count || 0);
  if (keyOnly > 0) {
    out.push({
      severity: "warning",
      id: "previous-output-columns",
      title: "Previous output columns",
      body:
        `Previous output columns — ${keyOnly} item(s) brought forward from the uploaded previous month's output show only their reference, policy, date, narration, name and amount; any other columns they had are not in that file, so this run cannot show them. ` +
        "Workbooks exported before 1 Oct 2026 dropped those columns from items they had themselves brought forward. To restore them, re-run that month (and any earlier month it was rolled forward from) on the current version and upload its new output. Totals are unaffected.",
    });
  }

  // The previous output's Others rows were kept unchecked rather than dropped (backend
  // prematch_stages): one also on its Cleared tab is then brought forward twice.
  if (result.prior_others_not_deduplicated) {
    out.push({
      severity: "critical",
      id: "prior-others-not-deduplicated",
      title: "Previous output Others",
      body:
        "Previous output Others — the Others rows of the uploaded previous month's output could not be checked against its Cleared tab, so an item listed on both tabs may be brought forward twice in this run. " +
        "Compare those two tabs of that file before relying on the Net Adjustment.",
    });
  }

  const merged = result.merged_warnings;
  if (Array.isArray(merged) && merged.length > 0) {
    out.push({
      severity: "critical",
      id: "merged-warnings",
      title: "Duplicate sheets",
      body:
        "Duplicate-sheet warning — these sheet pairs look like the same ledger uploaded twice (all sheets were still merged): " +
        merged
          .map((w) => `${w.sheet_a} ↔ ${w.sheet_b} (${Math.round(w.overlap * 100)}% overlap)`)
          .join(" · ") +
        ". Verify and remove the duplicate source sheet, then re-upload.",
    });
  }

  const header = result.header_warning;
  if (Array.isArray(header) && header.length > 0) {
    out.push({
      severity: "critical",
      id: "header-warning",
      title: "Header warning",
      body:
        `Header warning — expected column(s) missing or duplicated on the statement's header row: ${header.join(", ")}. ` +
        "The reconciliation ran on the columns present; verify the affected figures before relying on them.",
    });
  }

  const amount = result.amount_warning;
  if (Array.isArray(amount) && amount.length > 0) {
    const total = amount.reduce((n, w) => n + (w.count || 0), 0);
    out.push({
      severity: "critical",
      id: "amount-warning",
      title: "Amount warning",
      body:
        `Amount warning — ${total} cell(s) could not be read as numbers and were treated as 0.00, so totals may be understated. ` +
        amount.map((w) => `${w.context} (e.g. ${w.samples.slice(0, 3).join(", ")})`).join(" · ") +
        ". Fix the source cells and re-upload.",
    });
  }

  // A skip rule removed rows that carry a document number and money: possibly real
  // transactions, counted in no figure (§6). The engine's guard reports only numbered rows
  // of at least a cent, and is silent on every finance-verified golden, so this fires on
  // a real anomaly rather than on footers.
  const skipped = result.skip_warning;
  if (Array.isArray(skipped) && skipped.length > 0) {
    const total = skipped.reduce((n, w) => n + (w.count || 0), 0);
    const eg = skipped.flatMap((w) => w.samples ?? []).slice(0, 3)
      .map((s) => `${s.id} ${fmtAed(s.amount)}`);
    out.push({
      severity: "critical",
      id: "skip-warning",
      title: "Skipped rows",
      body:
        `Skipped rows — ${total} row(s) with a document number and an amount were read as total or heading lines and left out of every figure` +
        (eg.length ? ` (e.g. ${eg.join(", ")})` : "") +
        ". If any is a real transaction the totals are understated: check it on the statement and have the insurer's layout corrected.",
    });
  }

  // Matching kept the layout's sign frame over the data's. Critical: under the wrong frame
  // invoices pair with receipts (AL WATHBA Sep-26), and nothing else would show it.
  const signFrame = result.sign_frame_warning;
  if (Array.isArray(signFrame) && signFrame.length > 0) {
    const w = signFrame[0];
    const words = (f: string) => (f === "inverted" ? "opposite signs" : "the same sign");
    out.push({
      severity: "critical",
      id: "sign-frame-warning",
      title: "Sign direction",
      body:
        `Sign direction — the insurer's layout says its amounts and CRI's carry ${words(w.declared)}, but ${w.n_pairs} reference/policy-linked pairs in this upload read as ${words(w.data_frame)}. ` +
        "Matching kept the layout's setting. Check a few matched pairs (an invoice should pair with the insurer's debit note, not its receipt) and have the insurer's layout reviewed.",
    });
  }

  // Deliberately INFO, not a defect: the reference join covered most rows and the
  // rest fall through to the other matching tiers. No figure is affected, and the
  // wording carries no "verify the figures" call to action. This classification is
  // the reason the informational tier exists — it was the banner that made the flat
  // stack unreadable.
  const reference = result.reference_warning;
  if (Array.isArray(reference) && reference.length > 0) {
    const filled = reference.reduce((n, w) => n + (w.filled || 0), 0);
    const unresolved = reference.reduce((n, w) => n + (w.unresolved || 0), 0);
    out.push({
      severity: "info",
      id: "reference-warning",
      title: "Reference lookup",
      body:
        `Reference lookup — ${filled} row(s) were given their reference number from the companion file. ` +
        `${unresolved} row(s) were not listed in it and keep a blank reference; those are still ` +
        "reconciled using the other matching rules. Totals are unaffected.",
    });
  }

  // Stable sort by severity: equal severities keep the source order above, which is
  // the order finance already reads them in.
  return out
    .map((a, i) => ({ a, i }))
    .sort((x, y) => severityRank(x.a.severity) - severityRank(y.a.severity) || x.i - y.i)
    .map(({ a }) => a);
}
